import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Container, Row, Col, Card, Table, Modal, Button, Badge, OverlayTrigger, Tooltip, Alert } from "react-bootstrap";

import swal from "sweetalert";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import api from "../api/api";
import "../styles/management-pages.css";

const UserManagement = () => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [subUsers, setSubUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Current user info from localStorage
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  
  // Form states
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone_number: "",
    password: "",
    confirmPassword: "",
    role: "ROLE_USER",
    offer_type: currentUser.offer_type || "2",
    head: currentUser.id,
  });
  
  // Error states
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  // Add per-user action loading state
  const [actionLoading, setActionLoading] = useState({});
  // Friendly message state for non-critical fetch results (e.g. "no sub-users")
  const [fetchMessage, setFetchMessage] = useState("");

  // Fetch sub-users on component mount
  useEffect(() => {
    fetchSubUsers();
  }, []);

  const fetchSubUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/users/subusers/${currentUser.id}`);
      // Handle backend "no data" responses gracefully without swal
      if (response && response.data && response.data.type === "danger") {
        // treat as empty result and show friendly message in UI
        setSubUsers([]);
        setFetchMessage(response.data.message || "No sub-users yet.");
      } else {
        // response.data might be an array or an object with list
        const data = Array.isArray(response.data) ? response.data : response.data?.data || response.data || [];
        setSubUsers(data);
        setFetchMessage("");
      }
    } catch (error) {
      console.error("Error fetching sub-users:", error);
      
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    setErrors({
      ...errors,
      [name]: "",
    });
  };

  const validate = () => {
    let nameError = "";
    let emailError = "";
    let passwordError = "";
    let confirmPasswordError = "";

    if (!formData.name.trim()) {
      nameError = t("cannot_be_blank");
    }

    if (!formData.email.trim()) {
      emailError = t("cannot_be_blank");
    } else {
      const mailformat = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      if (!mailformat.test(formData.email)) {
        emailError = t("incorrect_email_format");
      }
    }

    if (formData.password.length < 6) {
      passwordError = t("password_min_length");
    }

    if (formData.password !== formData.confirmPassword) {
      confirmPasswordError = t("passwords_dont_match");
    }

    setErrors({
      name: nameError,
      email: emailError,
      password: passwordError,
      confirmPassword: confirmPasswordError,
    });

    return !nameError && !emailError && !passwordError && !confirmPasswordError;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/users/subusers", formData);
      
      if (response.data.type === "danger") {
        swal({
          icon: "error",
          title: t("Oops..."),
          text: response.data.message,
        });
        return;
      }

      if (response.data.type === "success") {
        swal({
          icon: "success",
          title: t("Success"),
          text: t("sub_user_created_success"),
        });
        setShowModal(false);
        resetForm();
        fetchSubUsers();
      }
    } catch (error) {
      swal({
        icon: "error",
        title: t("Error"),
        text: t("error_create_sub_user"),
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone_number: "",
      password: "",
      confirmPassword: "",
      role: "ROLE_USER",
      offer_type: currentUser.offer_type || "2",
      head: currentUser.id,
    });
    setErrors({
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
    });
  };

  const handleDeleteUser = async (user_uid) => {
    const willDelete = await swal({
      title: t("are_you_sure"),
      text: t("confirm_delete"),
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (willDelete) {
      try {
        await api.delete("/admin/delete-user", { data: { user_uid } });
        swal(t("Success"), t("delete_success_user"), "success");
        fetchSubUsers();
      } catch (error) {
        swal(t("Error"), t("cannot_delete"), "error");
      }
    }
  };

  // Helper to determine if a user is active
  // Backend uses '0' or '1' as strings
  const isActive = (user) => {
    if (user == null) return false;
    if (typeof user.is_active === "string") return user.is_active === "1";
    if (typeof user.is_active === "number") return user.is_active === 1;
    if (typeof user.active === "boolean") return user.active;
    if (user.status) {
      const s = String(user.status).toLowerCase();
      return s === "active" || s === "activated";
    }
    return true;
  };

  // Helper to determine if a user is confirmed
  // Backend uses is_valid field ('0' or '1' as strings)
  const isConfirmed = (user) => {
    if (user == null) return false;
    if (typeof user.is_valid === "string") return user.is_valid === "1";
    if (typeof user.is_valid === "number") return user.is_valid === 1;
    if (typeof user.confirmed === "boolean") return user.confirmed;
    if (typeof user.is_confirmed === "boolean") return user.is_confirmed;
    if (typeof user.email_verified === "boolean") return user.email_verified;
    return true;
  };

  // Activate user function
  const handleActivateUser = async (user_uid) => {
    const key = user_uid;
    setActionLoading((prev) => ({ ...prev, [key]: true }));
    
    try {
      const response = await api.post("/admin/activate-user", { user_uid });
      
      if (response.data.type === "success") {
        swal({
          icon: "success",
          title: t("Success"),
          text: t("user_activated_success") || "User activated successfully",
        });
        fetchSubUsers();
      } else if (response.data.type === "danger") {
        swal({
          icon: "error",
          title: t("oops") || "Error",
          text: t("error_activate_user") || "Failed to activate user",
        });
      }
    } catch (error) {
      console.error("Error activating user:", error);
      swal({
        icon: "error",
        title: t("oops") || "Error",
        text: t("error_activate_user") || "Failed to activate user",
      });
    } finally {
      setActionLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Deactivate user function
  const handleDeactivateUser = async (user_uid) => {
    const key = user_uid;
    setActionLoading((prev) => ({ ...prev, [key]: true }));
    
    try {
      const response = await api.post("/admin/desactivate-user", { user_uid });
      
      if (response.data.type === "success") {
        swal({
          icon: "success",
          title: t("Success"),
          text: t("user_deactivated_success") || "User deactivated successfully",
        });
        fetchSubUsers();
      } else if (response.data.type === "danger") {
        swal({
          icon: "error",
          title: t("oops") || "Error",
          text: t("error_deactivate_user") || "Failed to deactivate user",
        });
      }
    } catch (error) {
      console.error("Error deactivating user:", error);
      swal({
        icon: "error",
        title: t("oops") || "Error",
        text: t("error_deactivate_user") || "Failed to deactivate user",
      });
    } finally {
      setActionLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Confirm user function
  const handleConfirmUser = async (user_uid) => {
    const key = user_uid;
    setActionLoading((prev) => ({ ...prev, [key]: true }));
    
    try {
      const response = await api.post("/admin/confirm-user", { user_uid });
      
      if (response.data.type === "success") {
        swal({
          icon: "success",
          title: t("Success"),
          text: t("user_confirmed_success") || "User confirmed successfully",
        });
        fetchSubUsers();
      } else if (response.data.type === "danger") {
        swal({
          icon: "error",
          title: t("oops") || "Error",
          text: t("error_confirm_user") || "Failed to confirm user",
        });
      }
    } catch (error) {
      console.error("Error confirming user:", error);
      swal({
        icon: "error",
        title: t("oops") || "Error",
        text: t("error_confirm_user") || "Failed to confirm user",
      });
    } finally {
      setActionLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  return (
    <Container fluid className="management-page management-users-page">
      <Row>
        <Col>
          <Card className="management-card mb-4">
            <Card.Header className="management-card-header d-flex justify-content-between align-items-center">
              <div className="management-page-heading management-users-header-copy">
                <span className="management-page-eyebrow">SMARTFARM</span>
                <h1>{t("user_management")}</h1>
                <p>{t("sub-users-management") || t("Sub-Users")}</p>
              </div>
              <div className="management-users-header-actions">
                <Button
                  variant="primary"
                  className="management-primary-button"
                  onClick={() => setShowModal(true)}
                  disabled={loading}
                >
                  <i className="fas fa-plus" aria-hidden="true" />
                  {t("create_sub_user")}
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {/* Current User Info */}
              <div className="management-account-card mb-4">
                <span className="management-account-icon" aria-hidden="true">
                  <i className="fas fa-user" />
                </span>
                <div className="management-account-copy">
                  <span className="management-section-label">{t("main_account")}</span>
                  <div className="management-account-name">
                    <strong>{currentUser.name}</strong>
                    <Badge bg="primary">{t("main_user_badge")}</Badge>
                  </div>
                  <small className="text-muted">{currentUser.email}</small>
                </div>
              </div>

              {/* Sub-Users Table */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="management-subusers-title mb-0">{t("sub-users-management") || t("Sub-Users")}</h6>
                <small className="text-muted">{t("sub_users_count", { count: subUsers.length })}</small>
              </div>
               {loading ? (
                 <div className="management-empty-state text-center p-4">
                   <div className="spinner-border text-primary" role="status">
                     <span className="sr-only">{t("loading")}</span>
                   </div>
                 </div>
               ) : subUsers.length === 0 ? (
                 <div className="management-empty-state text-center p-4">
                  <i className="fa fa-users" style={{ fontSize: "3.5rem", opacity: 0.25 }}></i>
                  <h5 className="mt-3">{t("no_sub_users_title")}</h5>
                  {fetchMessage && <Alert variant="info" className="mt-2">{fetchMessage}</Alert>}
                  <p className="text-muted mt-2">{t("no_sub_users_desc")}</p>
                  <div className="mt-3">
                    <Button
                      variant="primary"
                      onClick={() => setShowModal(true)}
                    >
                      <i className="fa fa-plus mr-2"></i> {t("create_sub_user_cta")}
                    </Button>
                  </div>
                </div>
               ) : (
                 <Table responsive hover>
                   <thead style={{ backgroundColor: "#f8f9fa" }}>
                     <tr>
                       <th>{t("name")}</th>
                       <th>{t("email")}</th>
                       <th>{t("phone")}</th>
                       <th>{t("role")}</th>
                       <th>{t("status")}</th>
                       <th>{t("actions")}</th>
                     </tr>
                   </thead>
                   <tbody>
                     {subUsers.map((user) => {
                       const userKey = user.uid || user.id;
                       const userIsActive = isActive(user);
                       const userIsConfirmed = isConfirmed(user);
                       
                       return (
                         <tr key={userKey}>
                           <td>
                             <i className="fa fa-user-circle mr-2 text-muted"></i>
                             {user.name}
                           </td>
                           <td>{user.email}</td>
                           <td>{user.phone_number || "N/A"}</td>
                           <td>
                             <Badge bg="info">{user.role}</Badge>
                           </td>
                           <td>
                             {userIsActive ? (
                               <Badge bg="success">{t("active")}</Badge>
                             ) : (
                               <Badge bg="secondary">{t("inactive")}</Badge>
                             )}
                             {!userIsConfirmed && (
                               <Badge bg="warning" className="ml-2">{t("unconfirmed")}</Badge>
                             )}
                           </td>
                           <td>
                             <div className="d-flex" style={{ gap: "0.4rem" }}>
                              {/* Activate/Deactivate Button with tooltip */}
                              <OverlayTrigger overlay={<Tooltip>{userIsActive ? t("deactivate") : t("activate")}</Tooltip>}>
                                <span>
                                  <Button
                                    variant={userIsActive ? "outline-warning" : "outline-success"}
                                    size="sm"
                                    onClick={() => {
                                      if (userIsActive) {
                                        handleDeactivateUser(userKey);
                                      } else {
                                        handleActivateUser(userKey);
                                      }
                                    }}
                                    disabled={!!actionLoading[userKey]}
                                  >
                                    {actionLoading[userKey] ? (
                                      <span className="spinner-border spinner-border-sm" />
                                    ) : userIsActive ? (
                                      <><i className="fa fa-ban"></i> {t("deactivate")}</>
                                    ) : (
                                      <><i className="fa fa-check"></i> {t("activate")}</>
                                    )}
                                  </Button>
                                </span>
                              </OverlayTrigger>
 
                              {/* Confirm Button (only show if unconfirmed) with tooltip */}
                              {!userIsConfirmed && (
                                <OverlayTrigger overlay={<Tooltip>{t("confirm")}</Tooltip>}>
                                  <span>
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => handleConfirmUser(userKey)}
                                      disabled={!!actionLoading[userKey]}
                                    >
                                      {actionLoading[userKey] ? (
                                        <span className="spinner-border spinner-border-sm" />
                                      ) : (
                                        <><i className="fa fa-user-check"></i> {t("confirm")}</>
                                      )}
                                    </Button>
                                  </span>
                                </OverlayTrigger>
                              )}
 
                              {/* Delete Button with tooltip */}
                              <OverlayTrigger overlay={<Tooltip>{t("delete")}</Tooltip>}>
                                <span>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleDeleteUser(userKey)}
                                    disabled={!!actionLoading[userKey]}
                                  >
                                    <i className="fa fa-trash"></i>
                                  </Button>
                                </span>
                              </OverlayTrigger>
                             </div>
                           </td>
                         </tr>
                       );
                     })}
                   </tbody>
                 </Table>
               )}
             </Card.Body>
           </Card>
         </Col>
       </Row>

      {/* Create Sub-User Modal */}
      <Modal dialogClassName="management-user-modal" show={showModal} onHide={() => { setShowModal(false); resetForm(); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t("create_sub_user")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form className="management-user-form" onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <div className="form-group mb-3">
                  <label>{t("name")} *</label>
                  <input
                    type="text"
                    name="name"
                    className={`form-control ${errors.name ? "is-invalid" : ""}`}
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder={t("enter_full_name")}
                  />
                  {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                </div>
              </Col>
              <Col md={6}>
                <div className="form-group mb-3">
                  <label>{t("email")} *</label>
                  <input
                    type="email"
                    name="email"
                    className={`form-control ${errors.email ? "is-invalid" : ""}`}
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={t("enter_email_address")}
                  />
                  {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <div className="form-group mb-3">
                  <label>{t("password")} *</label>
                  <input
                    type="password"
                    name="password"
                    className={`form-control ${errors.password ? "is-invalid" : ""}`}
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder={t("enter_password")}
                  />
                  {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                </div>
              </Col>
              <Col md={6}>
                <div className="form-group mb-3">
                  <label>{t("confirm_password")} *</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    className={`form-control ${errors.confirmPassword ? "is-invalid" : ""}`}
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder={t("confirm_password_placeholder")}
                  />
                  {errors.confirmPassword && <div className="invalid-feedback">{errors.confirmPassword}</div>}
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <div className="form-group mb-3">
                  <label>{t("phone_number")}</label>
                  <PhoneInput
                    country={"tn"}
                    value={formData.phone_number}
                    onChange={(phone) => setFormData({ ...formData, phone_number: phone })}
                    inputStyle={{
                      width: "100%",
                    }}
                  />
                </div>
              </Col>
            </Row>

            <div className="alert alert-info">
              <i className="fa fa-info-circle mr-2"></i>
              {t("sub_user_info")}
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowModal(false); resetForm(); }}>
            {t("cancel")}
          </Button>
          <Button
            variant="primary"
            style={{ backgroundColor: "#26a6b7", borderColor: "#26a6b7" }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? t("Saving...") : t("create_sub_user")}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default UserManagement;
